/* Supply › Reports › Overview — stock value, and what the store traded.
 *
 * Two questions, in that order: what is on the shelves worth, and how did money
 * move over the window. Everything is scoped to the store selected in the topbar.
 *
 * Colour: the invoice's brand palette (navy + green), stepped to pass the
 * categorical checks against a white surface — green #43a047 (Sales), navy
 * #2E5A9E (Purchase), amber #b8821a (Profit). Fixed order, assigned per series and
 * never cycled, so a series keeps its colour whatever else is on the chart.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { cbFmtINR, cbFmtNum } from "../supplyData";
import { useWarehouse } from "../../Layout/WarehouseContext";
import {
  Button,
  Table,
  Tr,
  Td,
  SectionHeader,
  ToolbarBar,
  DateRangeFilter,
  Segmented,
  KpiBox,
  LoadingState,
  ErrorState,
  EmptyState,
  InlineAlert,
  IconDownload,
  IconPackage,
} from "../supplyKit";
import { exportToXlsx } from "./reportUtils";
import overviewService, { PAYMENT_MAX_DAYS } from "./overviewService";

/* Fixed series order — identity, never rank. */
const SERIES = [
  { key: "sales", label: "Sales", color: "#43a047" },
  { key: "purchase", label: "Purchase", color: "#2E5A9E" },
  { key: "profit", label: "Profit", color: "#b8821a" },
];

/* The server aggregates the whole window in one call and rejects anything longer
   than 366 days — checked here too, so an over-long range is a message on the
   filter rather than a round trip that comes back 400. */
const MAX_DAYS = 366;

/* Payment modes are nominal — the bar length already carries the magnitude, so
   every bar takes the same brand navy rather than spending eight hues on
   identity nobody needs to look up. One series, so no legend: the heading names
   it, and each bar is labelled with its own share. */
const PAYMENT_COLOR = "#2E5A9E";

const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const daysBetween = (a, b) =>
  Math.round((new Date(`${b}T00:00:00`) - new Date(`${a}T00:00:00`)) / 86400000) + 1;
/* "2026-08-17" -> "17/08", the axis label. */
const dayLabel = (d) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
/* Compact money for the axis — "1.2L" beats "120000" on a tick. */
const compactINR = (v) => {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  if (abs >= 1e7) return `${(n / 1e7).toFixed(1)}Cr`;
  if (abs >= 1e5) return `${(n / 1e5).toFixed(1)}L`;
  if (abs >= 1e3) return `${Math.round(n / 1e3)}k`;
  return String(Math.round(n));
};

const defaultRange = () => {
  const today = new Date();
  const from = new Date(today);
  from.setDate(today.getDate() - 29);
  return { fromDate: iso(from), toDate: iso(today) };
};

const Overview = () => {
  const { code: wh, isAll, name: whName } = useWarehouse();
  const [range, setRange] = useState(defaultRange);
  const [view, setView] = useState("chart");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  /* The payment breakdown loads on its own so a store or an environment without
     it cannot take the rest of the screen down with it. */
  const [payment, setPayment] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [paymentAvailable, setPaymentAvailable] = useState(true);

  const windowDays = daysBetween(range.fromDate, range.toDate);
  const windowError =
    !range.fromDate || !range.toDate
      ? "Pick a date range."
      : windowDays < 1
      ? "The end date is before the start date."
      : windowDays > MAX_DAYS
      ? `Range is ${windowDays} days — this overview covers at most ${MAX_DAYS}.`
      : null;
  /* The overview covers a year; the breakdown does not. Checked here so an
     over-long range is a line of copy on the panel, not a failed request. */
  const paymentWindowTooLong = windowDays > PAYMENT_MAX_DAYS;

  useEffect(() => {
    /* Stock value is per store: there is no all-stores inventory query, and the
       trade figures would then disagree with the tiles above them. */
    if (!wh || windowError) {
      setData(null);
      setLoading(false);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    overviewService
      .load({ warehouseId: wh, fromDate: range.fromDate, toDate: range.toDate })
      .then((res) => {
        if (!alive) return;
        setData(res);
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.response?.data?.message || e?.message || "Could not load the overview.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [wh, range.fromDate, range.toDate, windowError, reloadKey]);

  /* Second call, deliberately not folded into the one above: the breakdown is
     capped at a shorter window and is not deployed in every environment, so it
     has its own loading, its own error and its own silence. */
  useEffect(() => {
    if (!wh || windowError || paymentWindowTooLong) {
      setPayment(null);
      setPaymentLoading(false);
      return undefined;
    }
    let alive = true;
    setPaymentLoading(true);
    setPaymentError(null);
    overviewService
      .salesByPayment({ warehouseId: wh, fromDate: range.fromDate, toDate: range.toDate })
      .then((res) => {
        if (!alive) return;
        setPayment(res);
        setPaymentAvailable(true);
        setPaymentLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        /* 404 = this backend has no such report. Say nothing and drop the panel;
           an environment that never had the endpoint is not an error the user
           can act on. Anything else is a real failure and gets shown. */
        if (e?.response?.status === 404) {
          setPaymentAvailable(false);
          setPayment(null);
        } else {
          setPaymentError(
            e?.response?.data?.message || e?.message || "Could not load the payment breakdown."
          );
        }
        setPaymentLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [wh, range.fromDate, range.toDate, windowError, paymentWindowTooLong, reloadKey]);

  const trade = data?.trade;
  const valuation = data?.valuation;
  const series = useMemo(() => trade?.series || [], [trade]);
  const coveragePct = trade ? Math.round(trade.marginCoverage * 100) : 100;

  /* Amount and share ride along on the row so the bar can direct-label itself
     without a render callback. */
  const paymentRows = useMemo(
    () =>
      (payment?.rows || []).map((r) => ({
        ...r,
        valueLabel: `${cbFmtINR(r.amount)} · ${r.sharePct.toFixed(1)}%`,
      })),
    [payment]
  );

  const handleExport = () => {
    if (!series.length) return;
    exportToXlsx(
      `overview-${wh}-${range.fromDate}-to-${range.toDate}.xlsx`,
      ["Date", "Sales", "Purchase", "Profit"],
      series.map((d) => [d.date, d.sales, d.purchase, d.profit]),
      "Overview"
    );
  };

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionHeader
        title="Overview"
        sub={
          wh
            ? `${whName || wh} · ${range.fromDate} → ${range.toDate}`
            : isAll
            ? "Stock value is held per store — pick a single store"
            : "No store selected"
        }
        actions={
          <Button icon={<IconDownload size={14} />} onClick={handleExport} disabled={!series.length}>
            Export
          </Button>
        }
      />

      {/* Filters in one row above the charts. */}
      <ToolbarBar>
        <DateRangeFilter
          from={range.fromDate}
          to={range.toDate}
          onFromChange={(v) => setRange((r) => ({ ...r, fromDate: v }))}
          onToChange={(v) => setRange((r) => ({ ...r, toDate: v }))}
        />
        <Segmented
          options={[
            { id: "chart", label: "Chart" },
            { id: "table", label: "Table" },
          ]}
          value={view}
          onChange={setView}
        />
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {windowDays} day{windowDays !== 1 ? "s" : ""}
        </div>
      </ToolbarBar>

      {windowError && <InlineAlert>{windowError}</InlineAlert>}

      {!wh ? (
        <EmptyState
          icon={<IconPackage size={18} />}
          title={isAll ? "Pick a single store" : "Select a store"}
          hint="Stock value is held per store, so this overview reports on one store at a time. Choose one in the Store control in the top bar."
        />
      ) : loading ? (
        <LoadingState label="Adding up stock, sales and purchases…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : !trade ? null : (
        <>
          {/* Stock on hand — two valuations of the same shelves. */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))", gap: 10 }}>
            <KpiBox
              label="Inventory value by MRP"
              value={cbFmtINR(valuation.mrpValue)}
              sub={`${cbFmtNum(valuation.skuCount)} SKUs · ${cbFmtNum(valuation.lotCount)} batches`}
              variant="ink"
            />
            <KpiBox
              label="Inventory value by PTR"
              value={cbFmtINR(valuation.ptrValue)}
              sub="At purchase cost"
              variant="ink"
            />
            <KpiBox
              label="Sales"
              value={cbFmtINR(trade.salesAmount)}
              sub={`${cbFmtNum(trade.salesCount)} bill${trade.salesCount !== 1 ? "s" : ""} · incl. GST`}
              variant="green"
            />
            <KpiBox
              label="Purchase"
              value={cbFmtINR(trade.purchaseAmount)}
              sub={`${cbFmtNum(trade.purchaseCount)} invoice${trade.purchaseCount !== 1 ? "s" : ""} · incl. GST`}
              variant="ink"
            />
            <KpiBox
              label="Profit"
              value={cbFmtINR(trade.profit)}
              /* Say what the number is. "Profit" alone gets read as cash in hand. */
              sub={
                coveragePct >= 100
                  ? `Gross margin, ex-GST · ${trade.marginPct.toFixed(1)}%`
                  : `Gross margin on ${coveragePct}% of lines`
              }
              variant="amber"
            />
          </div>

          {coveragePct < 100 && (
            <InlineAlert tone="amber">
              Profit covers {coveragePct}% of sold lines. The rest could not be matched to a batch, so
              there is no cost price for them — those lines are left out of the margin entirely rather
              than counted as free stock. Expect 100% here; anything lower is worth reporting.
            </InlineAlert>
          )}

          {view === "chart" ? (
            <div
              style={{
                background: "var(--bg-panel)",
                border: "1px solid var(--border-default)",
                borderRadius: 6,
                padding: "14px 12px 8px",
              }}
            >
              <div style={{ height: 340 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={series} margin={{ top: 8, right: 16, bottom: 4, left: 8 }}>
                    {/* Recessive grid: horizontal only, so it reads as a reference
                        and not as a cage around the data. */}
                    <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="2 4" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={dayLabel}
                      tick={{ fontSize: 10, fill: "var(--fg-muted)" }}
                      axisLine={{ stroke: "var(--border-default)" }}
                      tickLine={false}
                      minTickGap={16}
                    />
                    {/* One axis. All three series are rupees; a second scale would
                        let any two of them be drawn into any relationship at all. */}
                    <YAxis
                      tickFormatter={compactINR}
                      tick={{ fontSize: 10, fill: "var(--fg-muted)" }}
                      axisLine={false}
                      tickLine={false}
                      width={52}
                    />
                    <Tooltip
                      cursor={{ stroke: "var(--border-strong)", strokeWidth: 1 }}
                      content={<DayTooltip />}
                    />
                    <Legend
                      verticalAlign="top"
                      align="left"
                      height={28}
                      iconType="plainline"
                      wrapperStyle={{ fontSize: 11, color: "var(--fg-muted)" }}
                    />
                    {SERIES.map((s) => (
                      <Line
                        key={s.key}
                        type="monotone"
                        dataKey={s.key}
                        name={s.label}
                        stroke={s.color}
                        strokeWidth={2}
                        /* No dot per point — 92 of them is texture, not data. The
                           hover dot is 8px, which is the size that matters. */
                        dot={false}
                        activeDot={{ r: 4, strokeWidth: 2, stroke: "var(--bg-panel)" }}
                        isAnimationActive={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            /* The table view is the same numbers, readable without colour — and
               what the export writes. */
            <Table
              columns={[
                { label: "Date" },
                { label: "Sales", align: "right" },
                { label: "Purchase", align: "right" },
                { label: "Profit", align: "right" },
              ]}
            >
              {series.map((d) => (
                <Tr key={d.date}>
                  <Td mono>{d.date}</Td>
                  <Td mono right>
                    {cbFmtINR(d.sales)}
                  </Td>
                  <Td mono right>
                    {cbFmtINR(d.purchase)}
                  </Td>
                  <Td mono right style={{ color: d.profit < 0 ? "var(--red-700)" : undefined }}>
                    {cbFmtINR(d.profit)}
                  </Td>
                </Tr>
              ))}
              {series.length === 0 && (
                <Tr>
                  <Td colSpan={4} style={{ textAlign: "center", color: "var(--fg-muted)", padding: 20 }}>
                    Nothing in this window.
                  </Td>
                </Tr>
              )}
            </Table>
          )}

          {/* Sales by payment — bifurcation. Same window as everything above,
              answering a different question: not how much was sold, but how it
              was paid for. Hidden entirely where the backend has no such report. */}
          {paymentAvailable && (
            <div
              style={{
                background: "var(--bg-panel)",
                border: "1px solid var(--border-default)",
                borderRadius: 6,
                padding: "12px 12px 10px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                  marginBottom: 10,
                }}
              >
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--fg-strong)" }}>
                    Sales by payment — bifurcation
                  </div>
                  <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 2 }}>
                    How the window's billed sales split across payment modes
                  </div>
                </div>
                {payment && !paymentWindowTooLong && (
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--fg-strong)" }}>
                      {cbFmtINR(payment.total.amount)}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--fg-muted)" }}>
                      {cbFmtNum(payment.total.invoiceCount)} bill
                      {payment.total.invoiceCount !== 1 ? "s" : ""} · incl. GST
                    </div>
                  </div>
                )}
              </div>

              {paymentWindowTooLong ? (
                <div style={{ fontSize: 12, color: "var(--fg-muted)", padding: "16px 0" }}>
                  The breakdown covers at most {PAYMENT_MAX_DAYS} days — this range is {windowDays}.
                  Narrow the dates to see it.
                </div>
              ) : paymentLoading ? (
                <LoadingState label="Splitting sales by payment mode…" />
              ) : paymentError ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 0", flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, color: "var(--red-700)" }}>{paymentError}</span>
                  <Button onClick={() => setReloadKey((k) => k + 1)}>Retry</Button>
                </div>
              ) : !paymentRows.length ? (
                <div style={{ fontSize: 12, color: "var(--fg-muted)", padding: "16px 0" }}>
                  No billed sales in this window.
                </div>
              ) : view === "chart" ? (
                /* fixed label gutters leave no room for bars on a phone, so the
                   chart keeps a minimum width and scrolls inside its card */
                <div className="overflow-x-auto cb-scroll">
                <div style={{ minWidth: 480 }}>
                <ResponsiveContainer width="100%" height={Math.max(132, paymentRows.length * 40 + 28)}>
                  <BarChart
                    data={paymentRows}
                    layout="vertical"
                    /* Right margin is the label gutter: wide enough for
                       "₹12,45,320.50 · 41.2%" at the longest bar, so the label
                       never sits on the fill. */
                    margin={{ top: 4, right: 142, bottom: 4, left: 0 }}
                    barCategoryGap={12}
                  >
                    <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="2 4" horizontal={false} />
                    <XAxis
                      type="number"
                      tickFormatter={compactINR}
                      tick={{ fontSize: 10, fill: "var(--fg-muted)" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="label"
                      width={124}
                      tick={{ fontSize: 11, fill: "var(--fg-default)" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip cursor={{ fill: "var(--bg-subtle)" }} content={<PaymentTooltip />} />
                    <Bar
                      dataKey="amount"
                      fill={PAYMENT_COLOR}
                      /* Rounded at the data end only — the baseline end stays
                         square so the bar reads as anchored to zero. */
                      radius={[0, 4, 4, 0]}
                      maxBarSize={22}
                      isAnimationActive={false}
                    >
                      {/* Plain <text> rather than position="right": recharts'
                          own label wraps a long value onto a second line at the
                          separator, which reads as two rows of data. */}
                      <LabelList dataKey="valueLabel" content={<BarValueLabel />} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                </div>
                </div>
              ) : (
                <Table
                  columns={[
                    { label: "Payment mode" },
                    { label: "Bills", align: "right" },
                    { label: "Amount", align: "right" },
                    { label: "Share", align: "right" },
                  ]}
                >
                  {paymentRows.map((r) => (
                    <Tr key={r.mode || r.label}>
                      <Td>{r.label}</Td>
                      <Td mono right>
                        {cbFmtNum(r.invoiceCount)}
                      </Td>
                      <Td mono right>
                        {cbFmtINR(r.amount)}
                      </Td>
                      <Td mono right>
                        {r.sharePct.toFixed(1)}%
                      </Td>
                    </Tr>
                  ))}
                </Table>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* Crosshair tooltip: all three series for the hovered day, so they can be compared
   without reading three separate hovers. */
const DayTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "var(--bg-panel)",
        border: "1px solid var(--border-default)",
        borderRadius: 6,
        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        padding: "8px 10px",
        fontSize: 11,
      }}
    >
      <div style={{ fontWeight: 700, color: "var(--fg-strong)", marginBottom: 6, fontFamily: "var(--font-mono)" }}>
        {label}
      </div>
      {SERIES.map((s) => {
        const row = payload.find((p) => p.dataKey === s.key);
        return (
          <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
            <span style={{ width: 10, height: 2, background: s.color, flexShrink: 0 }} />
            <span style={{ color: "var(--fg-muted)", minWidth: 58 }}>{s.label}</span>
            {/* Value in text ink, not the series colour — the swatch carries identity. */}
            <span style={{ marginLeft: "auto", fontFamily: "var(--font-mono)", color: "var(--fg-strong)" }}>
              {cbFmtINR(row?.value || 0)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

/* Amount and share, parked just past the bar's end. Ink, not the bar colour —
   the bar carries identity, the text carries the number. */
const BarValueLabel = ({ x = 0, y = 0, width = 0, height = 0, value }) => (
  <text
    x={x + width + 8}
    y={y + height / 2}
    dominantBaseline="central"
    style={{ fontSize: 11, fill: "var(--fg-default)", fontFamily: "var(--font-mono)" }}
  >
    {value}
  </text>
);

/* One mode's figures on hover — the label on the bar shows amount and share, so
   the tooltip's job is the bill count that has nowhere else to go. */
const PaymentTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const r = payload[0]?.payload;
  if (!r) return null;
  return (
    <div
      style={{
        background: "var(--bg-panel)",
        border: "1px solid var(--border-default)",
        borderRadius: 6,
        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        padding: "8px 10px",
        fontSize: 11,
      }}
    >
      <div style={{ fontWeight: 700, color: "var(--fg-strong)", marginBottom: 4 }}>{r.label}</div>
      <div style={{ display: "flex", gap: 10 }}>
        <span style={{ color: "var(--fg-muted)" }}>Amount</span>
        <span style={{ marginLeft: "auto", fontFamily: "var(--font-mono)", color: "var(--fg-strong)" }}>
          {cbFmtINR(r.amount)}
        </span>
      </div>
      <div style={{ display: "flex", gap: 10, marginTop: 2 }}>
        <span style={{ color: "var(--fg-muted)" }}>Bills</span>
        <span style={{ marginLeft: "auto", fontFamily: "var(--font-mono)", color: "var(--fg-strong)" }}>
          {cbFmtNum(r.invoiceCount)}
        </span>
      </div>
      <div style={{ display: "flex", gap: 10, marginTop: 2 }}>
        <span style={{ color: "var(--fg-muted)" }}>Share</span>
        <span style={{ marginLeft: "auto", fontFamily: "var(--font-mono)", color: "var(--fg-strong)" }}>
          {r.sharePct.toFixed(1)}%
        </span>
      </div>
    </div>
  );
};

export default Overview;
